import api from './api';

/**
 * Notification API Service
 * Handles all notification-related API calls
 */
const notificationService = {
  /**
   * Get paginated notifications for the current user
   * @param {Object} params - Query parameters (page, per_page, category, is_read, priority)
   */
  getNotifications: async (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null)
      )
    ).toString();
    const endpoint = `/notifications${queryString ? `?${queryString}` : ''}`;
    const response = await api.authRequest(endpoint, { method: 'GET' });
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return await response.json();
  },

  /**
   * Get unread notification count with category breakdown
   */
  getUnreadCount: async () => {
    const response = await api.authRequest('/notifications/unread-count', { method: 'GET' });
    if (!response.ok) throw new Error('Failed to fetch unread count');
    return await response.json();
  },

  /**
   * Mark a single notification as read
   * @param {number} notificationId
   */
  markAsRead: async (notificationId) => {
    const response = await api.authRequest(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
    if (!response.ok) throw new Error('Failed to mark notification as read');
    return await response.json();
  },

  /**
   * Mark all notifications as read (optionally by category)
   * @param {string|null} category - Optional category filter
   */
  markAllAsRead: async (category = null) => {
    const queryString = category ? `?category=${category}` : '';
    const response = await api.authRequest(`/notifications/read-all${queryString}`, {
      method: 'PUT',
    });
    if (!response.ok) throw new Error('Failed to mark all notifications as read');
    return await response.json();
  },

  /**
   * Mark multiple specific notifications as read
   * @param {number[]} notificationIds
   */
  markMultipleAsRead: async (notificationIds) => {
    const response = await api.authRequest('/notifications/read-multiple', {
      method: 'PUT',
      body: JSON.stringify({ notification_ids: notificationIds }),
    });
    if (!response.ok) throw new Error('Failed to mark notifications as read');
    return await response.json();
  },

  /**
   * Delete a single notification
   * @param {number} notificationId
   */
  deleteNotification: async (notificationId) => {
    const response = await api.authRequest(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete notification');
    return await response.json();
  },

  /**
   * Delete all read notifications older than specified days
   * @param {number} olderThanDays - Default 30
   */
  deleteOldNotifications: async (olderThanDays = 30) => {
    const response = await api.authRequest(`/notifications?older_than_days=${olderThanDays}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete old notifications');
    return await response.json();
  },
};

export default notificationService;
